/* eslint react/jsx-handler-names: "off" */
import React, {useEffect, useMemo, useState} from 'react';
import {Zoom} from '@visx/zoom';
import {localPoint} from '@visx/event';
import {
    generateWorkPackageDAGData
} from "../../../../utils/cpm-functions/set-cpm-map/functions/generateDAGData";
import * as d3Dag from "d3-dag";
import {Group} from "@visx/group";
import {useActiveProjectCalendarsSelector, useCpmMapSelector} from "../../../../store/selectors/project.selectors";
import {Popup, PopupContent, PopupHeader,} from 'semantic-ui-react'
import {convertIndexToSeconds} from "../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import {useDispatch} from "react-redux";
import {useAllTaskListSelector} from "../../../../store/selectors/task/task.selectors";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCrown, faExpand, faCompress} from "@fortawesome/pro-solid-svg-icons";
import {useProjectMemberListSelector} from "../../../../store/selectors/team.selectors";
import ProjectMembersModel from "../../../../models/responses/project-members.model";
import UserImage from "../../../../images/UserImage";
import {MemberOrganizationEnum} from "../../../../models/member-organization.enum";
import TaskStatusModel from "../../../../models/responses/task-status.model";
import {
    useWorkStreamsSelector
} from "../../../../store/selectors/work-packages.selectors";
import {WorkPackage} from "../../../../models/responses/work-package.model";
import moment from "moment";

const blue = "#4285f4";
const predStatusRed = "#d9001b";
const predStatusGreen = "#0f9d58";
const predStatusYellow = "#f4b400";
const lightpurple = "#374469";
const bg = '#CFE7D8';
const redBg = "#F6D1CE";
const background = "white"
const selected = 'rgba(203, 230, 255)'
const hoverColour = 'rgba(241, 248, 255)'

const nodeWidth = 180;
const nodeHeight = 60;

export type ZoomIProps = {
    width: number;
    initialHeight: number;
    workPackageRef: string | null;
    delay: boolean
    scope: string
    setShowDag: (show: boolean) => void
    setActiveGreenMilestone: (milestone: any) => void
    setActiveRedMilestone: (milestone: any) => void
    driving: boolean,
    navigateToTask: any,
    setDagLoaded: (loaded: boolean) => void
    workPackagesMap: Map<string, WorkPackage>;
    setSelectedWorkPackage: (workPackage: WorkPackage | null) => void
};

const handleStatusTypes = {
    [TaskStatusModel.COMPLETE]: 'completed',
    [TaskStatusModel.IN_PROGRESS]: 'in progress',
    [TaskStatusModel.NOT_STARTED]: 'not started',
    [TaskStatusModel.SUSPENDED]: 'in progress',
    [TaskStatusModel.BLOCK]: 'not started',
    [TaskStatusModel.DECLARED_COMPLETE]: 'completed',
}
export default function WorkPackageDag({
                                           width,
                                           initialHeight,
                                           workPackageRef,
                                           delay,
                                           scope,
                                           navigateToTask,
                                           setDagLoaded,
                                           workPackagesMap,
                                           setSelectedWorkPackage}: ZoomIProps) {
    const [dag, setDag] = useState<any | null>(null);
    const [height, setHeight] = useState(initialHeight);
    const [layoutWidth, setLayoutWidth] = useState(0);
    const [layoutHeight, setLayoutHeight] = useState(0);
    const cpmMap = useCpmMapSelector()
    const projectCalendars = useActiveProjectCalendarsSelector()
    const dispatch = useDispatch()
    const allTasks = useAllTaskListSelector();
    const projectMembers = useProjectMemberListSelector();
    const [translation, setTranslation] = useState({x: 0, y: 0})
    const zoomRef = React.useRef<any>(null);
    const [hoverWorkPackage, setHoverWorkPackage] = useState<string | null>(workPackageRef)
    const [hoverLink, setHoverLink] = useState<number | null>(null)
    const [hoverUser, setHoverUser] = useState<{email: string, x: number, y: number} | null>(null)
    const [fullScreen, setFullScreen] = useState(false)
    const workStreams = useWorkStreamsSelector();

    const projectMemberMap = useMemo(() => {
        let memberMap = new Map<string, ProjectMembersModel>();
        projectMembers.forEach(member => {
            memberMap.set(member.userId, member);
            memberMap.set(member.userEmail, member);
        });
        return memberMap;
    }, [projectMembers]);

    function getDateFromIndex(taskId: string) {
        const task = cpmMap.get(taskId)
        const date = convertIndexToSeconds(task?.ef, task?.cal_id, projectCalendars)
        if (task) {
            return new Date(date * 1000).toDateString()
        }
        return new Date().toDateString()
    }

    function centerOnSelectedNode(zoom: any, selectedNode: any) {
        if (!selectedNode) return;

        const { scaleX, scaleY } = zoom.transformMatrix;

        const centerX = width / 2;
        const centerY = height / 2;

        const newTranslateX = centerX - selectedNode.uy * scaleX;
        const newTranslateY = centerY - selectedNode.ux * scaleY;

        zoom.setTransformMatrix({
            scaleX,
            scaleY,
            translateX: newTranslateX,
            translateY: newTranslateY,
            skewX: 0,
            skewY: 0,
        });
    }


    function createChronologicalDecross(
        getStartDate: (id: string) => number | undefined
    ) {
        const baseDecross = d3Dag.decrossTwoLayer();

        return (layers: any[][]): void => {

            // First, perform the standard crossing minimisation.
            baseDecross(layers);

            // Cache start dates for real nodes.
            const dates = new Map<any, number>();

            for (const layer of layers) {
                for (const node of layer) {
                    if (node.data.role !== "node") continue;

                    const id = node.data.node.data.id;
                    const date = getStartDate(id);

                    if (date !== undefined && Number.isFinite(date)) {
                        dates.set(node, date);
                    }
                }
            }

            // Count crossings involving two adjacent nodes,
            // using their neighbours in the preceding/following layer.
            function countPairCrossings(
                neighboursA: any,
                neighboursB: any,
                positions: Map<any, number> | null
            ): [number, number] {

                if (!positions) return [0, 0];

                const aPositions = [...neighboursA]
                    .map(n => positions.get(n))
                    .filter((p): p is number => p !== undefined);

                const bPositions = [...neighboursB]
                    .map(n => positions.get(n))
                    .filter((p): p is number => p !== undefined);

                let before = 0;
                let after = 0;

                for (const a of aPositions) {
                    for (const b of bPositions) {
                        if (a > b) before++;
                        if (a < b) after++;
                    }
                }

                return [before, after];
            }

            // Make a few chronological optimisation passes.
            // Adjacent swaps are accepted only if crossings
            // remain unchanged or improve.
            for (let pass = 0; pass < 3; pass++) {
                let changed = false;

                for (let i = 0; i < layers.length; i++) {
                    const layer = layers[i];

                    const previousPositions = i > 0
                        ? new Map(layers[i - 1].map((n, j) => [n, j]))
                        : null;

                    const nextPositions = i < layers.length - 1
                        ? new Map(layers[i + 1].map((n, j) => [n, j]))
                        : null;

                    for (let j = 0; j < layer.length - 1; j++) {
                        const a = layer[j];
                        const b = layer[j + 1];

                        const dateA = dates.get(a);
                        const dateB = dates.get(b);

                        // Ignore dummy nodes and missing dates.
                        if (dateA === undefined || dateB === undefined)
                            continue;

                        // Already chronologically ordered.
                        if (dateA <= dateB) continue;

                        const [beforeParents, afterParents] =
                            countPairCrossings(
                                a.parents(),
                                b.parents(),
                                previousPositions
                            );

                        const [beforeChildren, afterChildren] =
                            countPairCrossings(
                                a.children(),
                                b.children(),
                                nextPositions
                            );

                        const crossingsBefore =
                            beforeParents + beforeChildren;

                        const crossingsAfter =
                            afterParents + afterChildren;

                        // Only swap when crossing count cannot increase.
                        if (crossingsAfter <= crossingsBefore) {
                            layer[j] = b;
                            layer[j + 1] = a;
                            changed = true;

                            // Recheck the preceding pair.
                            j = Math.max(-1, j - 2);
                        }
                    }
                }

                if (!changed) break;
            }
        };
    }

    type TimeColumnOptions = {
        maxColumns?: number;
        minBucketDays?: number;
        columnSpacing?: number;
        rowSpacing?: number;
    };

    function applyTimeColumnLayout(
        dag: any,
        getStartSeconds: (id: string) => number | undefined,
        options: TimeColumnOptions = {}
    ): { width: number; height: number } | null {

        const {
            maxColumns = 25,
            minBucketDays = 30,
            columnSpacing = 280,
            rowSpacing = 105
        } = options;

        const nodes: any[] = [...dag.nodes()];
        if (!nodes.length) return null;

        // Capture the original d3-dag vertical arrangement.
        // In this component, ux is the screen Y coordinate.
        const originalY = new Map<any, number>(
            nodes.map(n => [n, n.ux ?? 0])
        );

        const dates = new Map<any, number>();

        for (const node of nodes) {
            const date = getStartSeconds(node.data.id);

            if (date !== undefined && Number.isFinite(date)) {
                dates.set(node, date);
            }
        }

        const validDates = Array.from(dates.values());

        // Keep the standard layout if dates are unavailable.
        if (!validDates.length) return null;

        const minDate = Math.min(...validDates);
        const maxDate = Math.max(...validDates);

        const day = 86400;
        const week = day * 7;

        // Choose a suitable bucket size automatically.
        // The minimum is 30 days by default.
        const bucketSize = Math.max(
            minBucketDays * day,
            Math.ceil(
                (maxDate - minDate) /
                (Math.max(2, maxColumns) - 1) /
                week
            ) * week
        );

        const columns = new Map<any, number>();

        // Assign columns in dependency order.
        // A child cannot appear LEFT of its parents,
        // but it may share their column.
        for (const node of dag.topological()) {
            const date = dates.get(node);

            let column = date === undefined
                ? 0
                : Math.floor((date - minDate) / bucketSize);

            for (const parent of node.parents()) {
                column = Math.max(
                    column,
                    columns.get(parent) ?? 0
                );
            }

            columns.set(node, column);
        }

        // Group nodes into their assigned columns.
        const groups = new Map<number, any[]>();

        for (const node of nodes) {
            const column = columns.get(node) ?? 0;

            if (!groups.has(column)) {
                groups.set(column, []);
            }

            groups.get(column)!.push(node);
        }

        const minOriginalY = Math.min(...Array.from(originalY.values()));
        const maxOriginalY = Math.max(...Array.from(originalY.values()));
        const originalRange = Math.max(
            1,
            maxOriginalY - minOriginalY
        );

        const placedRanks = new Map<any, number>();
        let maxRows = 0;

        const columnIndexes = Array.from(groups.keys())
            .sort((a, b) => a - b);

        for (const column of columnIndexes) {
            const group = groups.get(column)!;
            const groupSet = new Set(group);

            // Number of parents in the SAME column.
            const remainingParents = new Map<any, number>();

            for (const node of group) {
                const count = [...node.parents()]
                    .filter(p => groupSet.has(p))
                    .length;

                remainingParents.set(node, count);
            }

            const ready = group.filter(
                n => remainingParents.get(n) === 0
            );

            const ordered: any[] = [];

            // Preserve as much of the original layout's
            // ordering as possible, while encouraging
            // children to align with their predecessors.
            const score = (node: any): number => {
                const original =
                    ((originalY.get(node) ?? 0) - minOriginalY)
                    / originalRange;

                const earlierParents = [...node.parents()]
                    .filter(p =>
                        (columns.get(p) ?? 0) < column &&
                        placedRanks.has(p)
                    );

                if (!earlierParents.length) {
                    return original;
                }

                const parentAverage =
                    earlierParents.reduce(
                        (sum, p) => sum + placedRanks.get(p)!,
                        0
                    ) / earlierParents.length;

                // Original layout has greater influence.
                return 0.75 * original + 0.25 * parentAverage;
            };

            // Dependency-constrained vertical sorting.
            while (ready.length) {
                ready.sort((a, b) =>
                    score(a) - score(b) ||
                    String(a.data.id).localeCompare(
                        String(b.data.id)
                    )
                );

                const node = ready.shift()!;
                ordered.push(node);

                for (const child of node.children()) {
                    if (!groupSet.has(child)) continue;

                    const remaining =
                        (remainingParents.get(child) ?? 0) - 1;

                    remainingParents.set(child, remaining);

                    if (remaining === 0) {
                        ready.push(child);
                    }
                }
            }

            if (ordered.length !== group.length) {
                throw new Error(
                    "Unable to order date column: cycle detected"
                );
            }

            // Apply the resulting coordinates.
            ordered.forEach((node, index) => {
                node.uy = 140 + column * columnSpacing;
                node.ux = 80 + index * rowSpacing;

                placedRanks.set(
                    node,
                    ordered.length === 1
                        ? 0.5
                        : index / (ordered.length - 1)
                );
            });

            maxRows = Math.max(maxRows, ordered.length);
        }

        const lastColumn = Math.max(...columnIndexes);

        return {
            width: 280 + lastColumn * columnSpacing,
            height: 160 + (maxRows - 1) * rowSpacing
        };
    }



    useEffect(() => {
        const dagData = generateWorkPackageDAGData(
            Array.from(workPackagesMap.values()),
            cpmMap,
            allTasks
        );

        const dag = d3Dag.graphStratify()(dagData);

        const nodeRadius = 30;

        const layout = d3Dag
            .sugiyama()
            .layering(d3Dag.layeringLongestPath())
            .decross(d3Dag.decrossTwoLayer())
            .nodeSize((node) => [
                (node ? 3.8 : 0.25) * nodeRadius,
                8 * nodeRadius
            ]);

        const originalSize = layout(dag as any);

        const dateSize = applyTimeColumnLayout(
            dag,
            (id: string) => {
                const wp = workPackagesMap.get(id);
                const taskId = wp?.drivingTaskStart?.task_id;

                if (!taskId) return undefined;

                const task = cpmMap.get(taskId);
                if (task?.es == null) return undefined;

                const seconds = convertIndexToSeconds(
                    task.es,
                    task.cal_id,
                    projectCalendars
                );

                return Number.isFinite(seconds)
                    ? seconds
                    : undefined;
            },
            {
                maxColumns: 25,
                minBucketDays: 30,
                columnSpacing: 280,
                rowSpacing: 105
            }
        );

        setDag(dag);

        setLayoutWidth(dateSize?.width ?? originalSize.height);
        setLayoutHeight(dateSize?.height ?? originalSize.width);

    }, [workPackageRef, width]);

    const links = useMemo(() => {
        if (!dag) return [];
        let outLinks: any[] = []
        for (const link of dag.links()) {
            outLinks.push(link);
        }
        return outLinks
    }, [dag]);

    const nodes = useMemo(() => {
        if (!dag) return [];
        let outNodes: any[] = []
        for (const node of dag.nodes()) {
            outNodes.push(node);
        }
        return outNodes
    }, [dag]);

    useEffect(() => {
        if (!dag) return;
        if (fullScreen) {
            setHeight(window.innerHeight * 0.7);
        } else {
            setHeight(initialHeight);
        }
    }, [fullScreen]);

    const initialTransform = useMemo(() => {
        if (!dag) return {
            scaleX: 1.0,
            scaleY: 1.0,
            translateX: 0,
            translateY: 0,
            skewX: 0,
            skewY: 0,
        }
        const centerX = width / 2;
        const centerY = height / 2;
        const selectedNode = dag.nodes().find((node: any) => node.data.id === workPackageRef);
        const newTranslateX = centerX - selectedNode.uy
        const newTranslateY = centerY - selectedNode.ux

        return {
            scaleX: 1,
            scaleY: 1,
            translateX: newTranslateX,
            translateY: newTranslateY,
            skewX: 0,
            skewY: 0,
        }
    }, [dag])

    const hoverWorkPackageData = useMemo(() => {
        if (hoverWorkPackage) {
            const workPackage = workPackagesMap.get(hoverWorkPackage)
            if (workPackage && workPackage.drivingTaskStart) {
                return {wp: workPackage, task: workPackage.drivingTaskStart}
            }
            return {wp: workPackage, task: null}
        } else {
            return null
        }
    }, [hoverWorkPackage, workPackagesMap])

    // use effect to centre plot after node change
    useEffect(() => {
        if (!workPackageRef || !nodes.length || !zoomRef.current) return;

        const selectedNode = nodes.find(node => node.data.id === workPackageRef);
        if (selectedNode) {
            centerOnSelectedNode(zoomRef.current, selectedNode);
        }

        setDagLoaded(true)
    }, [dag])

    return dag ? (
        <Zoom<SVGSVGElement>
            width={layoutWidth}
            height={layoutHeight}
            scaleXMin={1 / 4}
            scaleXMax={4}
            scaleYMin={1 / 4}
            scaleYMax={4}
            initialTransformMatrix={initialTransform}
        >
            {(zoom) => {
                zoomRef.current = zoom;
                return (
                    <div className="relative" onMouseLeave={() => setHoverWorkPackage(null)}>
                        <svg
                            width={width}
                            height={height}
                            style={{cursor: zoom.isDragging ? 'grabbing' : 'auto', touchAction: 'none'}}
                            ref={zoom.containerRef}
                        >
                            <defs>
                                <marker
                                    id="arrow"
                                    viewBox="0 -5 10 10"
                                    refX="10"
                                    refY="0"
                                    markerWidth="7"
                                    markerHeight="7"
                                    orient="auto"
                                    fill={lightpurple}
                                >
                                    <path d="M0,-5L10,0L0,5Z"/>
                                </marker>
                                <filter id="f4" width="120" height="120">
                                    <feOffset in="SourceGraphic" dx="3" dy="3" />
                                    <feColorMatrix type="matrix" values = "0.2 0 0 0 0 0 0.2 0 0 0 0 0 0.2 0 0 0 0 0 1 0"/>
                                    <feGaussianBlur stdDeviation="2" />
                                    <feBlend in="SourceGraphic" in2="blurOut" />
                                </filter>
                            </defs>
                            <rect width={width} height={height} fill={scope === "preds" ? "white" : delay ? redBg : bg}/>
                            <rect
                                width={width}
                                height={height}
                                fill="transparent"
                                onTouchStart={zoom.dragStart}
                                onTouchMove={zoom.dragMove}
                                onTouchEnd={zoom.dragEnd}
                                onMouseDown={zoom.dragStart}
                                onMouseMove={zoom.dragMove}
                                onMouseUp={zoom.dragEnd}
                                onMouseLeave={() => {
                                    if (zoom.isDragging) zoom.dragEnd();
                                }}
                                onDoubleClick={(event) => {
                                    const point = localPoint(event) || {x: 0, y: 0};
                                    zoom.scale({scaleX: 1.1, scaleY: 1.1, point});
                                }}
                            />
                            <Group transform={zoom.toString()}>

                                {links.map((link, i) => {
                                    const predWorkPackage = workPackagesMap.get(link.source.data.id);
                                    const succWorkPackage = workPackagesMap.get(link.target.data.id);

                                    const predTask = predWorkPackage?.drivingTaskStart
                                        ? cpmMap.get(predWorkPackage.drivingTaskStart.task_id)
                                        : null;

                                    const succTask = succWorkPackage?.drivingTaskStart
                                        ? cpmMap.get(succWorkPackage.drivingTaskStart.task_id)
                                        : null;

                                    if (!predTask || !succTask) return null;

                                    const sourceX = link.source.uy;
                                    const sourceY = link.source.ux;
                                    const targetX = link.target.uy;
                                    const targetY = link.target.ux;

                                    const sameColumn = Math.abs(sourceX - targetX) < 1;

                                    let path: string;

                                    if (sameColumn) {
                                        // Straight vertical connection between stacked nodes
                                        const x = sourceX;

                                        const y1 = sourceY + nodeHeight / 2;
                                        const y2 = targetY - nodeHeight / 2;

                                        path = `M ${x} ${y1} L ${x} ${y2}`;
                                    } else {
                                        // Links between different columns
                                        const x1 = sourceX + nodeWidth / 2;
                                        const x2 = targetX - nodeWidth / 2;
                                        const dx = Math.max(20, (x2 - x1) / 2);

                                        path = `
                                            M ${x1} ${sourceY}
                                            C ${x1 + dx} ${sourceY},
                                              ${x2 - dx} ${targetY},
                                              ${x2} ${targetY}
                                        `;
                                    }

                                    const isDashed =
                                        handleStatusTypes[predTask.status_code] === TaskStatusModel.COMPLETE ||
                                        handleStatusTypes[succTask.status_code] === TaskStatusModel.COMPLETE;

                                    return (
                                        <path
                                            key={`link-${i}`}
                                            d={path}
                                            stroke={lightpurple}
                                            strokeWidth={hoverLink === i ? 2 : 1}
                                            strokeDasharray={isDashed ? '5, 5' : 'none'}
                                            fill="none"
                                            markerEnd="url(#arrow)"
                                            onMouseOver={() => setHoverLink(i)}
                                        />
                                    );
                                })}

                                {nodes.map((node, i) => {
                                    const workPackage = workPackagesMap.get(node.data.id)
                                    const task = workPackage && workPackage.drivingTaskStart ? cpmMap.get(workPackage?.drivingTaskStart?.task_id) : null
                                    return task ? (
                                        <Popup
                                            key={`node-${i}`}
                                            open={hoverWorkPackage === node.data.id}
                                            onMouseLeave={() => setHoverWorkPackage(null)}
                                            trigger={
                                                <Group
                                                    top={node.ux}
                                                    left={node.uy}
                                                    style={{cursor: "pointer"}}
                                                    onClick={(e) => {
                                                        // const activeTaskInput = allTasks.get(node.data.id) || null;
                                                        setTranslation({x: zoom.transformMatrix.translateX, y: zoom.transformMatrix.translateY});
                                                        setSelectedWorkPackage(workPackage || null)
                                                        // navigateToTask(activeTaskInput)
                                                        centerOnSelectedNode(zoom, node)
                                                        e.stopPropagation()
                                                    }}
                                                    onMouseOver={() => setHoverWorkPackage(node.data.id)}
                                                    // onMouseOut={() => setHoverWorkPackage(null)}
                                                >
                                                    <rect
                                                        height={nodeHeight}
                                                        width={nodeWidth}
                                                        y={-nodeHeight / 2}
                                                        x={-nodeWidth / 2}
                                                        fill={node.data.id === workPackageRef ? selected :
                                                            hoverWorkPackage === node.data.id ? hoverColour :
                                                                background}
                                                        opacity={1}
                                                        stroke={task?.predStatus === 1 ? predStatusGreen :
                                                            task?.predStatus === 2 ? predStatusYellow :
                                                                task?.predStatus === 3 ? predStatusRed : blue}
                                                        strokeWidth={1}
                                                        rx={5}
                                                        filter={"url(#f4)"}
                                                    />
                                                    <path
                                                        d={`M-${nodeWidth / 2 - 5},-${nodeHeight / 2 - 1}
                                                       h15
                                                       v${nodeHeight - 2}
                                                       h-15
                                                       q-5,0 -5,-5
                                                       v-${nodeHeight - 12}
                                                       q0,-5 5,-5
                                                       z
                                                    `}
                                                        fill={task?.predStatus === 1 ? predStatusGreen :
                                                            task?.predStatus === 2 ? predStatusYellow :
                                                                task?.predStatus === 3 ? predStatusRed : blue}
                                                        opacity={1}
                                                        stroke={task?.predStatus === 1 ? predStatusGreen :
                                                            task?.predStatus === 2 ? predStatusYellow :
                                                                task?.predStatus === 3 ? predStatusRed : blue}
                                                        strokeWidth={2}
                                                        // filter={"url(#f4)"}
                                                    />
                                                    <foreignObject
                                                        width={nodeWidth - 20}
                                                        height={nodeHeight}
                                                        x={-nodeWidth / 2 + 20}
                                                        y={-nodeHeight / 2}
                                                    >
                                                        <div style={
                                                            {
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                height: nodeHeight,
                                                                width: nodeWidth - 20,
                                                                overflow: "hidden",
                                                                pointerEvents: "none",
                                                                gap: "5px",
                                                            }
                                                        }>
                                                            <p style={{
                                                                pointerEvents: "none",
                                                                textAlign: "center",
                                                                fontSize: 14,
                                                            }}>
                                                                {workPackage?.workPackageName}
                                                            </p>
                                                        </div>
                                                    </foreignObject>
                                                </Group>
                                            }>
                                            {hoverWorkPackageData && <PopupHeader>{
                                                workStreams.find(ws => hoverWorkPackageData.wp && ws.workStreamId === hoverWorkPackageData.wp.workStreamId)?.workStreamName
                                                || "No Work Stream"
                                            }</PopupHeader>}
                                            {hoverWorkPackageData && <PopupContent>
                                                {hoverWorkPackageData.wp && hoverWorkPackageData.wp.workPackageName}
                                                {hoverWorkPackageData.wp && hoverWorkPackageData.wp.workPackageOwner && <div style={{
                                                    background: '#a6a6a6',
                                                    color: '#fff',
                                                    fontSize: '12px',
                                                    padding: '1px 5px',
                                                    maxHeight: '20px',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    textAlign: "center",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    marginBottom: '2px',
                                                }}
                                                ><FontAwesomeIcon icon={faCrown}
                                                                  style={{
                                                                      marginRight: '5px',
                                                                      color: '#fff',
                                                                      fontSize: '12px',
                                                                  }}
                                                />{hoverWorkPackageData && hoverWorkPackageData.wp && (projectMemberMap.get(hoverWorkPackageData.wp.workPackageOwner)?.userEmail || hoverWorkPackageData.wp.workPackageOwner)}</div>}
                                                {hoverWorkPackageData.task && (hoverWorkPackageData.task.suspendReason || hoverWorkPackageData.task.overdueReason) ?
                                                    <div style={{
                                                        background: '#333333',
                                                        color: '#fff',
                                                        fontSize: '12px',
                                                        padding: '1px 5px',
                                                        maxHeight: '20px',
                                                        textAlign: "center",
                                                        justifyContent: "center",
                                                        display: "flex",
                                                    }}>{hoverWorkPackageData.task && (hoverWorkPackageData.task.suspendReason || hoverWorkPackageData.task.overdueReason)}</div>
                                                    : null}
                                                {hoverWorkPackageData.task && hoverWorkPackageData.task.early_start_date && <div
                                                style={{
                                                    background: '#ceecc4',
                                                    color: '#000',
                                                    fontSize: '12px',
                                                    padding: '1px 5px',
                                                    maxHeight: '20px',
                                                    textAlign: "center",
                                                    justifyContent: "center",
                                                    display: "flex",
                                                }}>
                                                    {`Start: ${moment(hoverWorkPackageData.task.early_start_date.toDate()).format("DD MMM YYYY")}`}
                                                </div>}
                                                <div style={{display: "grid", gridTemplateColumns: "repeat(8, 26px)", marginTop: "5px"}}>
                                                    {hoverWorkPackageData.wp && hoverWorkPackageData.wp.workPackageTaskForce && 
                                                        hoverWorkPackageData.wp.workPackageTaskForce.map(user => {
                                                        const projectMember = projectMemberMap.get(user)
                                                        if (projectMember && projectMember.grade !== MemberOrganizationEnum.GROUPS) {
                                                            return (<div
                                                                key={projectMember.userId}
                                                                onMouseOver={(e) => {
                                                                    setHoverUser({
                                                                        email: projectMember?.userEmail,
                                                                        x: e.clientX,
                                                                        y: e.clientY,
                                                                    })}}
                                                                onMouseLeave={() => setHoverUser(null)}>
                                                                <UserImage
                                                                    user={projectMember}
                                                                    key={user}
                                                                    width={25}
                                                                    height={25}
                                                                /></div>)
                                                        }
                                                        return null
                                                    })}
                                                </div>
                                            </PopupContent>}
                                        </Popup>
                                    ) : null
                                })}
                            </Group>
                        </svg>
                        <div className="controls">
                            <button
                                type="button"
                                className="btn btn-zoom"
                                onClick={() => zoom.scale({scaleX: 1.2, scaleY: 1.2})}
                            >
                                +
                            </button>
                            <button
                                type="button"
                                className="btn btn-zoom btn-bottom"
                                onClick={() => zoom.scale({scaleX: 0.8, scaleY: 0.8})}
                            >
                                -
                            </button>
                            <button type="button" className="btn btn-lg" onClick={zoom.reset}>
                                Reset
                            </button>
                            <button style={{position: "absolute", top: `${height - 40}px`}} type="button" className="btn btn-lg" onClick={() => {
                                setHoverWorkPackage(fullScreen ? null : workPackageRef)
                                const currentTransform = zoom.transformMatrix
                                if (fullScreen) {
                                    zoom.setTransformMatrix({
                                        scaleX: currentTransform.scaleX,
                                        scaleY: currentTransform.scaleY,
                                        translateX: currentTransform.translateX,
                                        translateY: currentTransform.translateY + (initialTransform.translateY - currentTransform.translateY),
                                        skewX: 0,
                                        skewY: 0,
                                    })
                                }
                                setFullScreen(!fullScreen)
                            }}>
                                {fullScreen ? <FontAwesomeIcon icon={faCompress} size={'lg'} /> : <FontAwesomeIcon icon={faExpand} size={'lg'} />}
                            </button>
                        </div>
                        {hoverUser && <div style={{
                            background: '#333333',
                            color: '#fff',
                            fontSize: '12px',
                            padding: '1px 5px',
                            maxHeight: '20px',
                            overflow: 'hidden',
                            position: 'fixed',
                            top: `${hoverUser.y + 35}px`,
                            left: `${hoverUser.x}px`,
                            zIndex: 1500,
                        }}>{hoverUser.email}</div>}
                    </div>
                )}}
        </Zoom>
    ) : null;
}
